// doctor-frontend/src/components/ProtectedRoute.js
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { clearCmfmSession } from '../services/auth';

/**
 * ProtectedRoute
 * - Accepts all historical auth token keys + loggedInUser fallback (no regressions).
 * - DOES NOT clear storage (matches your existing behavior).
 * - Listens for `auth:unauthorized` (dispatched by http.js) and redirects to login,
 *   preserving the "from" location so you can bounce back post-login.
 *
 * Fixes (non-breaking):
 * - Reads tokens from BOTH localStorage + sessionStorage (some flows store token in session).
 * - Accepts cmfm_token key (current CMFM login stores it).
 * - Tenant-aware login path: /t/:slug + /cmfm/login
 * - Avoids stale kick state once user becomes authed again.
 *
 * NEW (Dec 2025 - no regression):
 * - Only kick-to-login when event indicates a true 401 Unauthorized.
 *   (Prevents false "logout" when backend returns 403/404/500 but http.js misfires the event.)
 */

const BASE_LOGIN_PATH = process.env.REACT_APP_LOGIN_PATH || '/cmfm/login';

function pick(store, key) {
  try {
    return store.getItem(key) || '';
  } catch {
    return '';
  }
}

function getToken() {
  // ✅ expanded, non-breaking: include cmfm_token + sessionStorage
  // ✅ IMPORTANT: doctor-frontend must NOT accept citizen tokens (prevents cross-portal auto-login)
  // ✅ Option A: Prefer CMFM token keys first, then legacy/generic keys
  const keys = ['cmfmToken', 'cmfm_token', 'authToken', 'token'];

  // localStorage first
  for (const k of keys) {
    const v = pick(localStorage, k);
    if (v) return v;
  }

  // sessionStorage fallback
  for (const k of keys) {
    const v = pick(sessionStorage, k);
    if (v) return v;
  }

  // loggedInUser fallback (some older flows store token there)
  // ✅ IMPORTANT (Option A): doctor-frontend must NOT authenticate using a citizen session.
  // If loggedInUser indicates role === "citizen", ignore its token and fall through (not authed).
  try {
    const lu = JSON.parse(localStorage.getItem('loggedInUser') || 'null');
    const su = JSON.parse(sessionStorage.getItem('loggedInUser') || 'null');
    const obj = (lu && (lu.user || lu)) || (su && (su.user || su)) || null;

    const role = String(obj?.role || '').toLowerCase().trim();
    if (role === 'citizen') return '';

    const t = obj && (obj.token || obj.jwt || obj.accessToken);
    if (t) return t;
  } catch {}

  return '';
}

function getTenantSlugFromPath(pathname) {
  try {
    const m = String(pathname || '').match(/^\/t\/([^/]+)/);
    return m?.[1] ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

function buildLoginPath(pathname) {
  const slug = getTenantSlugFromPath(pathname);
  const base = BASE_LOGIN_PATH.startsWith('/') ? BASE_LOGIN_PATH : `/${BASE_LOGIN_PATH}`;
  if (!slug) return base;
  return `/t/${encodeURIComponent(slug)}${base}`;
}

export default function ProtectedRoute() {
  const location = useLocation();
  const [kicked, setKicked] = useState(false);

  const token = useMemo(() => getToken(), [location.pathname, kicked]);
  const isAuthed = !!token;

  // Listen for unauthorized event fired by http.js
  useEffect(() => {
    function onUnauthorized(e) {
      const status = e?.detail?.status;
      // Only kick if a true 401. During an explicit logout the layout owns the
      // redirect, so ignore the expected logout-request 401 and avoid a race.
      if (status === 401) {
        try {
          if (window.__cmfmLogoutInProgress) return;
        } catch {}

        // A 401 means the stored CMFM token is no longer usable. Remove only
        // CMFM/staff authentication keys; keep tenant selection and any citizen
        // portal session untouched.
        clearCmfmSession();
        setKicked(true);
      }
    }

    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  // If user becomes authed again, clear stale kick state
  useEffect(() => {
    if (isAuthed && kicked) {
      setKicked(false);
    }
  }, [isAuthed, kicked]);

  if (!isAuthed || kicked) {
    const to = buildLoginPath(location.pathname);
    return <Navigate to={to} replace state={{ from: location }} />;
  }

  return <Outlet />;
}
